/**
 * Paint helpers.
 *
 * Every colour that reaches the DOM goes through here, so the two rules are
 * enforced in one place: a swatch always declares the ink that sits on it, and
 * the room's `--live` glow only ever comes from a real colour.
 */

import { Lab, hexToOklab, isDark, relativeLuminance } from '../core/colour';
import { hexToRgb } from '../core/colour';

export interface Swatchable {
  hex: string;
  lab: Lab;
}

/** CSS variables for a colour block: the fill, and readable ink on top of it. */
export function swatch(hexOrLab: Swatchable | string): Record<string, string> {
  const hex = typeof hexOrLab === 'string' ? hexOrLab : hexOrLab.hex;
  const lab = typeof hexOrLab === 'string' ? hexToOklab(hexOrLab) : hexOrLab.lab;
  return {
    '--swatch': hex,
    '--on-swatch': isDark(lab) ? '#ffffff' : '#0b0b0e',
  };
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Push a colour into the room. The background glow, the pips, the primary
 * buttons and the answer ring all read from these, so one call retints the
 * whole studio.
 */
export function applyLive(hex: string, lab: Lab = hexToOklab(hex)): void {
  const root = document.documentElement.style;
  root.setProperty('--live', hex);
  root.setProperty('--live-soft', rgba(hex, 0.5));
  root.setProperty('--live-glow', rgba(hex, 0.2));
  // Button text has to survive being painted on the live colour.
  root.setProperty('--live-ink', relativeLuminance(lab) > 0.35 ? '#0b0b0e' : '#ffffff');
}

export function clearLive(): void {
  const root = document.documentElement.style;
  root.setProperty('--live', '#8b8b96');
  root.setProperty('--live-soft', rgba('#8b8b96', 0.5));
  root.setProperty('--live-glow', rgba('#8b8b96', 0.2));
  root.setProperty('--live-ink', '#ffffff');
}

/** A soft tint of a colour, for card corners and eyebrow glows. */
export function glowVar(hex: string, alpha = 0.22): string {
  return rgba(hex, alpha);
}

export { rgba };
